"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CloudWalletHttpClient = void 0;
const ethers_1 = require("ethers");
const utils_1 = require("./utils");
const cross_fetch_1 = __importDefault(require("cross-fetch"));
/**
 * An HTTP client used to communicate with the wallet service in the Cloud.
 */
class CloudWalletHttpClient {
    /**
     *
     * @param network The network that the provider should interact with. This can be an integer (chain ID) or a string (e.g., "goerli")
     * @param apiKey The API key for the CloudWallet service
     */
    constructor(apiKey) {
        this.apiKey = apiKey;
        if (CloudWalletHttpClient.internalFetch === undefined) {
            CloudWalletHttpClient.internalFetch = cross_fetch_1.default;
        }
    }
    /**
     * Get the ethers Provider
     * @param network The network that the provider should interact with. This can be an integer (chain ID) or a string (e.g., "goerli")
     *
     * @returns ethers Provider
     */
    getProvider(network) {
        console.log("getProvider(network=" + network + ")");
        const chainId = ethers_1.Network.from(network).chainId;
        const URL = `${CloudWalletHttpClient.endpointUrl}/${this.apiKey}/rpc?chainId=${chainId}`;
        return new ethers_1.JsonRpcProvider(URL, network, { batchMaxCount: 1, staticNetwork: true, polling: true }); // Cloudwallet provider does not support batching, network is fixed, use polling (but we don't use events anyway)
    }
    /**
     *
     * @returns The attestation report for the CloudWallet enclave
     */
    async getAttestationReport() {
        const res = await (await CloudWalletHttpClient.internalFetch(`${CloudWalletHttpClient.endpointUrl}/${this.apiKey}/attestation-report`, {
            credentials: "include"
        })).json();
        return res;
    }
    /**
     *
     * @returns The verified PCR0s according to the CloudWallet service
     */
    async getVerifiedPCR0s() {
        const res = await (await CloudWalletHttpClient.internalFetch(`${CloudWalletHttpClient.endpointUrl}/${this.apiKey}/pcr0`, {
            credentials: "include"
        })).json();
        return res;
    }
    /**
     * Create a new wallet controlled by the enclave in the Cloud
     * @param devicePublicKey The public key that will be used to verify instructions related to the newly created wallet
     * @returns The response returned by the CloudWallet service
     */
    async createWallet(body, signature, encKey) {
        return await CloudWalletHttpClient.internalFetch(`${CloudWalletHttpClient.endpointUrl}/${this.apiKey}/wallets`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CloudWallet-Signature": signature,
                "X-CloudWallet-EncKey": encKey
            },
            body: body,
            credentials: "include"
        });
    }
    async getMnemonic(signature, encryptedBlob, encKey) {
        return await CloudWalletHttpClient.internalFetch(`${CloudWalletHttpClient.endpointUrl}/${this.apiKey}/wallets`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "X-CloudWallet-Signature": signature,
                "X-CloudWallet-Encrypted-Blob": encryptedBlob,
                "X-CloudWallet-EncKey": encKey
            },
            credentials: "include"
        });
    }
    /**
     * Make a request to have a payload remotely signed with the wallet private key
     * @param body The body of the request
     * @param signature The signature on the body, produced with the client private key
     * @param encryptedBlob The encrypted blob for the wallet that should sign the payload
     * @returns The response returned by the CloudWallet service
     */
    async sendSignedRequest(body, signature, encryptedBlob, endpoint) {
        return await CloudWalletHttpClient.internalFetch(`${CloudWalletHttpClient.endpointUrl}/${this.apiKey}/${endpoint}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CloudWallet-Signature": signature,
                "X-CloudWallet-Encrypted-Blob": encryptedBlob
            },
            body: body,
            credentials: "include"
        });
    }
    /**
     * Obtain the enclave's public key, as specified in the attestation report. Note: during the execution of this call, the attesation report is NOT validated.
     * @param compressed Whether the key should be returned in a compressed form or not. Defaults to false.
     * @returns The enclave's public key, in exadecimal. It starts with 02 or 03 for the compressed form, or 04 for the uncompressed one.
     */
    async getEnclavePubKey(compressed) {
        const compressedKey = (await this.getAttestationReport())["publicKey"];
        if (compressed) {
            return compressedKey;
        }
        return (0, utils_1._compressedToUncompressedPubKey)(compressedKey);
    }
}
exports.CloudWalletHttpClient = CloudWalletHttpClient;
